<template>
  <section id="your-skin" class="your-skin-section ws-grid">
    <section-title :title="$t('section.yourSkin.title.part1')" class="your-skin-section__title" />

    <div class="your-skin-section__medical-data medical-data">
      <div
        v-if="photoTypeDisplayValue"
        class="medical-data__item data-item"
        @click="openDiscoverSidebar($options.discoverComponent.SKIN_PHOTOTYPE)"
      >
        <span class="data-item__type u-typography-helvetica u-text u-text--m">{{
          $t('welcomePage.infoSection.skinPhototype.yourIsLabel')
        }}</span>
        <span class="data-item__value data-item__value--link">{{ photoTypeDisplayValue }}</span>
      </div>
      <div
        v-if="skinThicknessDisplayValue"
        class="medical-data__item data-item"
        @click="openDiscoverSidebar($options.discoverComponent.SKIN_THICKNESS)"
      >
        <span class="data-item__type u-typography-helvetica u-text u-text--m">{{
          $t('welcomePage.infoSection.skinThickness.yourIsLabel')
        }}</span>
        <span class="data-item__value data-item__value--link">{{ skinThicknessDisplayValue }}</span>
      </div>
      <div v-if="uvExposureDisplayValue" class="medical-data__item data-item">
        <span class="data-item__type u-typography-helvetica u-text u-text--m">{{
          $t('welcomePage.infoSection.uvExposure.label')
        }}</span>
        <span class="data-item__value">{{ uvExposureDisplayValue }}</span>
      </div>
    </div>

    <div class="your-skin-section__diagnostic diagnostic">
      <div class="diagnostic__chart diagnostic-chart">
        <spider-chart
          class="diagnostic-chart__spider"
          interactive
          :active-dysfunction="activeDysfunction"
          :dysfunctions="allDysfunctions"
          @dysfunction-click="onDysfunctionClick"
        />
      </div>
      <swiper-type-four
        ref="swiper"
        class="diagnostic__swiper swiper"
        :items="slides"
        @change="onPageChange"
      >
        <template v-slot:item="{ item }">
          <summary-slide
            v-if="item.isSummary"
            class="swiper__slide"
            :dysfunctions="foundDysfunctions"
          />
          <dysfunction-description-slide
            v-else
            class="swiper__slide"
            :score="item.score"
            :dysfunction-name="item.dysfunctionName"
          />
        </template>
      </swiper-type-four>
    </div>

    <sidebar-popup ref="discoverPopup">
      <component :is="discoverComponent" />
    </sidebar-popup>
  </section>
</template>

<script>
import { differenceInMonths } from 'date-fns';

import { SpiderChart } from 'universkin-shared';
import { spiderChartDysfunctionsMixin } from 'universkin-shared/src/commonDomainObjects/mixins/spiderChartDysfunctionsMixin';

import SwiperTypeFour from '@/components/common/app-swiper/SwiperTypeFour.vue';
import SummarySlide from '@/components/your-skin/dysfunction-slide/SummarySlide.vue';
import DysfunctionDescriptionSlide from '@/components/your-skin/dysfunction-slide/DysfunctionDescriptionSlide.vue';
import SectionTitle from '@/components/common/SectionTitle.vue';

import { DYSFUNCTION_LABEL_KEY } from '@/constants/dysfunctions';

const DISCOVER_COMPONENT = {
  SKIN_PHOTOTYPE: 'SkinPhototypeDiscover',
  SKIN_THICKNESS: 'SkinThicknessDiscover'
};

const PHOTOTYPE_DISPLAY_VALUE = {
  phototype1: 'welcomePage.infoSection.skinPhototype.type1',
  phototype2: 'welcomePage.infoSection.skinPhototype.type2',
  phototype3: 'welcomePage.infoSection.skinPhototype.type3',
  phototype4: 'welcomePage.infoSection.skinPhototype.type4',
  phototype5: 'welcomePage.infoSection.skinPhototype.type5',
  phototype6: 'welcomePage.infoSection.skinPhototype.type6'
};
const THICKNESS_DISPLAY_VALUE = {
  thick: 'welcomePage.infoSection.skinThickness.thickLabel',
  thin: 'welcomePage.infoSection.skinThickness.thinLabel'
};
const UV_EXPOSURE_DISPLAY_VALUE = {
  veryMild: 'welcomePage.infoSection.uvExposure.veryMildLabel',
  mild: 'welcomePage.infoSection.uvExposure.mildLabel',
  moderate: 'welcomePage.infoSection.uvExposure.moderateLabel',
  severe: 'welcomePage.infoSection.uvExposure.severeLabel',
  verySevere: 'welcomePage.infoSection.uvExposure.verySevereLabel'
};

export default {
  name: 'YourSkinSectionTemplate',
  components: {
    SpiderChart,
    SwiperTypeFour,
    SummarySlide,
    DysfunctionDescriptionSlide,
    SectionTitle,
    SidebarPopup: () => import('@/components/common/SidebarPopup.vue'),
    SkinPhototypeDiscover: () => import('@/components/welcome-page/SkinPhototypeDiscover.vue'),
    SkinThicknessDiscover: () => import('@/components/welcome-page/SkinThicknessDiscover.vue')
  },
  mixins: [spiderChartDysfunctionsMixin],
  discoverComponent: DISCOVER_COMPONENT,
  props: {
    scores: {
      type: Object,
      required: true
    },
    photoType: {
      type: String,
      default: ''
    },
    skinThickness: {
      type: String,
      default: ''
    },
    uvExposure: {
      type: String,
      default: ''
    },
    diagnosticDate: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      activeDysfunction: '',
      discoverComponent: ''
    };
  },
  computed: {
    allDysfunctions() {
      return this.getDysfunctionsList(this.scores);
    },
    foundDysfunctions() {
      return Object.entries(this.scores)
        .map(([dysfunctionName, score]) => ({ dysfunctionName, score }))
        .filter(({ score }) => score > 0)
        .sort((firstElement, secondElement) => {
          const { dysfunctionName: firstElementName, score: firstElementScore } = firstElement;
          const { dysfunctionName: secondElementName, score: secondElementScore } = secondElement;

          if (secondElementScore === firstElementScore) {
            const firstElementNameLabel = this.$t(DYSFUNCTION_LABEL_KEY[firstElementName]);
            const secondElementNameLabel = this.$t(DYSFUNCTION_LABEL_KEY[secondElementName]);

            return firstElementNameLabel.localeCompare(secondElementNameLabel);
          }

          return secondElementScore - firstElementScore;
        });
    },
    slides() {
      return [{ isSummary: true }, ...this.foundDysfunctions];
    },
    photoTypeDisplayValue() {
      return this.$t(PHOTOTYPE_DISPLAY_VALUE[this.photoType]);
    },
    skinThicknessDisplayValue() {
      return this.$t(THICKNESS_DISPLAY_VALUE[this.skinThickness]);
    },
    uvExposureDisplayValue() {
      return this.hasOutdatedDiagnosticDate || !this.uvExposure
        ? ''
        : this.$t(UV_EXPOSURE_DISPLAY_VALUE[this.uvExposure]);
    },
    hasOutdatedDiagnosticDate() {
      if (!this.diagnosticDate) {
        return false;
      }

      return differenceInMonths(new Date(), new Date(this.diagnosticDate)) >= 2;
    }
  },
  methods: {
    onDysfunctionClick(dysfunctionName) {
      this.activeDysfunction = dysfunctionName;

      const page = this.slides.findIndex(({ dysfunctionName: name }) => dysfunctionName === name);

      this.$refs.swiper.navigateToPage(page);
    },
    onPageChange(page) {
      this.activeDysfunction = this.slides[page].dysfunctionName;
    },
    openDiscoverSidebar(componentName) {
      this.discoverComponent = componentName;
      this.$refs.discoverPopup.open();
    }
  }
};
</script>

<style lang="scss" scoped>
.your-skin-section {
  color: var(--u-color-grey-900);
  padding: 40px var(--ws-grid-m, 16px);

  &__title {
    margin-bottom: 24px;
  }

  &__medical-data {
    margin-bottom: 32px;
  }
}

.medical-data {
  &__item:not(:last-child) {
    margin-bottom: 12px;
  }
}

.data-item {
  display: flex;
  justify-content: space-between;
  align-items: baseline;

  &__type {
    color: var(--u-color-grey-600);
  }

  &__value {
    font-family: var(--u-font-family-Helvetica);
    font-weight: bold;
    font-size: 16px;
    line-height: 28px;
    letter-spacing: 0.4px;

    color: var(--u-color-black-5);

    &--link {
      text-decoration-line: underline;
      cursor: pointer;
    }
  }
}

.diagnostic {
  &__chart {
    margin-bottom: 32px;
  }

  &__swiper {
    max-width: 450px;
  }
}

.diagnostic-chart {
  display: flex;
  justify-content: center;
  align-items: flex-start;

  &__spider {
    flex: 0 0 100%;
  }
}

@media (min-width: 600px) {
  .your-skin-section {
    &__title {
      margin-bottom: 32px;
    }
  }

  .diagnostic {
    &__chart {
      width: var(--ws-col-6);
      margin-inline-start: auto;
      margin-inline-end: auto;
    }

    &__swiper {
      margin-inline-start: auto;
      margin-inline-end: auto;
    }
  }
}

@media (min-width: 960px) {
  .your-skin-section {
    &__title {
      width: 654px;
      margin-bottom: 16px;
    }

    &__medical-data {
      margin-bottom: 81px;
    }
  }

  .medical-data {
    display: flex;

    &__item:not(:last-child) {
      margin-inline-end: 42px;
    }
  }

  .data-item {
    &__type {
      margin-inline-end: 16px;
    }

    &__value {
      font-size: 18px;
      line-height: 23px;
    }
  }

  .diagnostic {
    display: flex;
    justify-content: flex-start;

    &__chart {
      margin-inline-start: 0;
      margin-inline-end: 0;
    }

    &__swiper {
      width: var(--ws-col-5);
      margin-inline-start: calc(var(--ws-col-1) + var(--ws-grid-g) * 2);
      margin-inline-end: 0;
    }
  }
}

@media (min-width: 1920px) {
  .your-skin-section {
    &__title {
      margin-bottom: 40px;
    }
  }
}
</style>
